import {
  Alert,
  Button,
  DateRangePicker,
  DateRangePickerProps,
  Form,
  FormField,
  Grid,
  Input,
  Select,
  SpaceBetween,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import { useFormik } from 'formik';
import _ from 'lodash';
import moment from 'moment';
import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useDeepCompareEffect from 'use-deep-compare-effect';
import * as yup from 'yup';
import { ExceptionStatusOptions, ExceptionTypeOptionsProps } from '../config';
import { getDateFromDateRange } from '../helper';
import { SearchFilterValues } from '../types';
import { DATE_RANGE_PICKER_LABELS, DATE_RANGE_PICKER_RELATIVE_OPTIONS } from './config';

export const SearchFilterFormSchema = yup.object().shape({
  title: yup.string(),
  createdBy: yup.string(),
  reference: yup.string(),
  assignedToId: yup.string(),
  site: yup.string(),
  model: yup.string(),
});

export type TableFilterProps = {
  onSearch: (filters: SearchFilterValues) => Promise<void>;
  filters: SearchFilterValues;
  loading: boolean;
  exceptionTypes: ExceptionTypeOptionsProps[];
};

export default function TableFilter(props: TableFilterProps): ReactElement {
  const { onSearch, filters, loading, exceptionTypes } = props;
  const { t } = useTranslation();
  const [filterCount, setFilterCount] = useState<number>(0);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [submittedFilters, setSubmittedFilters] = useState<SearchFilterValues>();
  const [filtersChanged, setFiltersChanged] = useState<boolean>(false);

  const getFilterCount = (filterValues: SearchFilterValues) => {
    return _.reduce(
      filterValues,
      (result, value, key) => {
        if (key !== 'title' && value && (value as OptionDefinition).value !== '') {
          return result + 1;
        }
        return result;
      },
      0
    );
  };

  const form = useFormik<SearchFilterValues>({
    initialValues: filters,
    validationSchema: SearchFilterFormSchema,
    onSubmit: async (filterValues) => {
      await onSearch(filterValues);
      setSubmittedFilters(filterValues);
      setFilterCount(getFilterCount(filterValues));
    },
  });

  const isFiltersEmpty = _.values(form.values).every(_.isEmpty);

  useDeepCompareEffect(() => {
    setFiltersChanged(
      !_.isEqual(_.omit(submittedFilters, ['title']), _.omit(form.values, ['title']))
    );
  }, [form.values, submittedFilters]);

  const handleClearAll = () => {
    onSearch({} as SearchFilterValues);
    form.resetForm();
    setFiltersChanged(false);
    setFilterCount(0);
    setSubmittedFilters(undefined);
  };

  const isValidRange = (value: DateRangePickerProps.Value | null) => {
    if (!value) return { valid: true } as DateRangePickerProps.ValidRangeResult;
    const { startDate, endDate } = getDateFromDateRange(value);
    if (moment(endDate).diff(startDate) >= 0) {
      return { valid: true } as DateRangePickerProps.ValidRangeResult;
    }
    return {
      valid: false,
      errorMessage: t('Invalid date range'),
    } as DateRangePickerProps.InvalidRangeResult;
  };

  const handleDateChange = (detail: DateRangePickerProps.ChangeDetail) => {
    form.setFieldValue('createdAt', detail.value);
  };

  return (
    <SpaceBetween direction="vertical" size="xl">
      <Grid gridDefinition={[{ colspan: 4 }, { colspan: 8 }]}>
        <TextFilter
          className="search-bar"
          filteringPlaceholder="Find exceptions"
          onChange={(e) => {
            form.setFieldValue('title', e.detail.filteringText);
          }}
          filteringText={form.values.title as string}
        />
        <SpaceBetween direction="horizontal" size="xl">
          <Button
            data-test-id="button-search"
            onClick={() => form.handleSubmit()}
            disabled={loading}>
            {t('Search')}
          </Button>
          <Button
            data-test-id="button-filters"
            iconAlign="left"
            iconName="filter"
            onClick={() => {
              setShowFilters(!showFilters);
            }}>
            {filterCount ? `(${filterCount}) ` : ' '}
            {t('Filters')}
          </Button>
        </SpaceBetween>
      </Grid>
      {showFilters && (
        <Form
          data-test-id="form-filters"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                data-test-id="button-clear-all"
                disabled={isFiltersEmpty}
                variant="normal"
                onClick={handleClearAll}>
                {t('Clear All')}
              </Button>
              <Button
                data-test-id="button-apply-filters"
                disabled={!filtersChanged || loading}
                variant="primary"
                onClick={() => form.handleSubmit()}>
                {t('Apply filters')}
              </Button>
            </SpaceBetween>
          }>
          {!_.isEmpty(form.errors) && (
            <Alert type="error">
              {Object.entries(form.errors).map(([key, value]) => (
                <li>
                  {key} - {value}{' '}
                </li>
              ))}
            </Alert>
          )}
          <Grid gridDefinition={Array(8).fill({ colspan: 3 })}>
            <FormField label={t('Status')}>
              <Select
                data-test-id="filter-status"
                options={ExceptionStatusOptions}
                selectedOption={form.values.status}
                onChange={(e) => {
                  form.setFieldValue('status', e.detail.selectedOption);
                }}
              />
            </FormField>
            <FormField label={t('Exception Type')}>
              <Select
                data-test-id="filter-exception-type"
                options={exceptionTypes}
                selectedOption={form.values.exceptionType}
                onChange={(e) => {
                  form.setFieldValue('exceptionType', e.detail.selectedOption);
                }}
              />
            </FormField>
            <FormField label={t('Site')} errorText={form.touched.site && form.errors.site}>
              <Input
                data-test-id="filter-site"
                value={form.values.site}
                onChange={(e) => {
                  form.setFieldValue('site', e.detail.value);
                  form.setFieldTouched('site');
                }}
                placeholder={t('Provide site name')}
              />
            </FormField>
            <FormField label={t('Reference')}>
              <Input
                data-test-id="filter-reference"
                value={form.values.reference}
                onChange={(e) => {
                  form.setFieldValue('reference', e.detail.value);
                }}
                placeholder={t('Provide PO or Shipment ID')}
              />
            </FormField>
            <FormField label={t('Model')}>
              <Input
                data-test-id="filter-model"
                value={form.values.model}
                onChange={(e) => {
                  form.setFieldValue('model', e.detail.value);
                }}
                placeholder={t('Provide model')}
              />
            </FormField>
            <FormField
              label={t('Created By')}
              errorText={form.touched.createdBy && form.errors.createdBy}>
              <Input
                data-test-id="filter-created-by"
                value={form.values.createdBy}
                onChange={(e) => {
                  form.setFieldValue('createdBy', e.detail.value);
                  form.setFieldTouched('createdBy');
                }}
                placeholder={t('Provide user alias')}
              />
            </FormField>
            <FormField
              label={t('Assigned To')}
              errorText={form.touched.assignedToId && form.errors.assignedToId}>
              <Input
                data-test-id="filter-assigned-to"
                value={form.values.assignedToId}
                onChange={(e) => {
                  form.setFieldValue('assignedToId', e.detail.value);
                  form.setFieldTouched('assignedToId');
                }}
                placeholder={t('Provide resolver group email')}
              />
            </FormField>
            <FormField label={t('Creation Date')}>
              <DateRangePicker
                data-test-id="filter-created-at"
                isValidRange={isValidRange}
                relativeOptions={DATE_RANGE_PICKER_RELATIVE_OPTIONS}
                onChange={({ detail }) => handleDateChange(detail)}
                value={form.values.createdAt}
                i18nStrings={DATE_RANGE_PICKER_LABELS}
                placeholder={t('Filter by a date')}
              />
            </FormField>
          </Grid>
        </Form>
      )}
    </SpaceBetween>
  );
}
